import { useState, memo } from "react";
import toast from "react-hot-toast";
import { Card, Badge, Button, Stars, Avatar, RouteDisplay, TimerBadge, T } from "../ui";
import { STATUS_CONFIG, formatTimestamp, timeLeftMins } from "../../utils/helpers";
import {
  submitOffer, selectCaptain, submitDepositProof,
  approveDeposit, rejectDeposit, completeTrip,
  cancelTrip, rateTrip, captainCompleteTrip,
} from "../../services/tripService";
import { uploadFile } from "../../services/storageService";
import { useSettings } from "../../contexts/SettingsContext";
import { useAuth } from "../../contexts/AuthContext";
import CarModal from "./CarModal";

const TripCard = memo(function TripCard({ trip, captainProfiles = {} }) {
  const { user } = useAuth();
  const settings = useSettings();

  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [offerPrice, setOfferPrice] = useState("");
  const [offerNote, setOfferNote] = useState("");
  const [txFile, setTxFile] = useState(null);
  const [rateVal, setRateVal] = useState(0);
  const [carModal, setCarModal] = useState(null);

  const st = STATUS_CONFIG[trip.status] || STATUS_CONFIG.pending;
  const role = user?.role;
  const myOffer = trip.offers?.find(o => o.captainId === user?.id);

  const searchLeft = trip.status === "pending" && trip.createdAt
    ? timeLeftMins(trip.createdAt, settings.searchLimitMinutes) : null;
  const depositLeft = trip.status === "awaiting_deposit" && trip.captainSelectedAt
    ? timeLeftMins(trip.captainSelectedAt, settings.depositLimitMinutes) : null;
  const reviewLeft = trip.status === "deposit_review" && trip.transferSubmittedAt
    ? timeLeftMins(trip.transferSubmittedAt, (settings.depositReviewLimitHours || 24) * 60) : null;

  // دالة موحدة لتنفيذ الأوامر مع حماية من التكرار
  async function doAction(fn, ...args) {
    if (busy) return; // منع الضغط المتكرر
    setBusy(true);
    try {
      await fn(...args);
      // تم نقل الـ Success Toast إلى داخل الدوال المتخصصة لضمان الدقة
    }
    catch (err) {
      toast.error(err.message || "حدث خطأ");
    }
    finally {
      setBusy(false);
    }
  }

  async function handleMakeOffer() {
    if (searchLeft <= 0) {
      toast.error("عذراً، انتهى وقت التقديم على هذه الرحلة");
      return;
    }
    if (!offerPrice || isNaN(+offerPrice) || +offerPrice <= 0) {
      toast.error("يرجى إدخال سعر صحيح"); return;
    }

    await doAction(async () => {
      await submitOffer(trip.id, {
        captainId: user.id, captainName: user.name,
        carLabel: `${user.car || ""} • ${user.plate || ""}`,
        rating: user.rating || 0,
        price: +offerPrice, note: offerNote,
      });
      toast.success("✓ تم إرسال عرضك!");
      setShowForm(false); setOfferPrice(""); setOfferNote("");
    });
  }

  async function handleSelectCaptain(offer) {
    const depositAmount = Math.max(
      settings.depositMin || 0,
      Math.min(settings.depositMax || 9999, settings.depositFixed || 100)
    );
    await doAction(async () => {
      await selectCaptain(trip.id, {
        captainId: offer.captainId, captainName: offer.captainName,
        price: offer.price, depositAmount,
      });
      toast.success("✓ تم اختيار الكابتن");
    });
  }

  async function handleSubmitProof() {
    if (!txFile) { toast.error("يرجى اختيار صورة التحويل"); return; }
    if (busy) return;

    setBusy(true);
    try {
      toast.loading("جاري رفع الصورة...", { id: "up" });
      const url = await uploadFile(txFile, `deposits/${trip.id}/${Date.now()}`);
      toast.dismiss("up");
      await submitDepositProof(trip.id, url);
      toast.success("📤 تم إرسال صورة التحويل للمراجعة");
      setTxFile(null);
    } catch (err) {
      toast.dismiss("up");
      toast.error(err.message || "فشل الرفع");
    }
    setBusy(false);
  }

  async function handleRate() {
    if (!rateVal) { toast.error("يرجى اختيار تقييم"); return; }
    await doAction(async () => {
      await rateTrip(trip.id, trip.selectedCaptain, rateVal);
      toast.success("⭐ شكراً على تقييمك!");
    });
  }

  const selCap = trip.selectedCaptain ? captainProfiles[trip.selectedCaptain] : null;
  const isConfirmed = ["confirmed", "captain_completed", "completed"].includes(trip.status);

  return (
    <>
      {carModal && <CarModal captain={carModal} onClose={() => setCarModal(null)} />}
      <Card>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar name={trip.clientName} size={38} color={T.info} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{trip.clientName}</div>
              <div style={{ fontSize: 11, color: T.muted }}>{formatTimestamp(trip.createdAt)}</div>
            </div>
          </div>
          <Badge color={st.color} bg={st.bg}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: st.dot, display: "inline-block" }} />
            {st.label}
          </Badge>
        </div>

        {/* Timers */}
        {searchLeft !== null && <div style={{ marginBottom: 10 }}><TimerBadge label="متبقي للبحث" mins={searchLeft} warnAt={15} /></div>}
        {depositLeft !== null && <div style={{ marginBottom: 10 }}><TimerBadge label="متبقي لدفع الديبوزت" mins={depositLeft} warnAt={10} /></div>}
        {reviewLeft !== null && <div style={{ marginBottom: 10 }}><TimerBadge label="متبقي لمراجعة رايدو" mins={reviewLeft} warnAt={60} /></div>}

        {/* Route */}
        <div style={{ marginBottom: 12 }}>
          <RouteDisplay from={trip.from} to={trip.to} />
        </div>

        {/* Details grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
          {[["📅", trip.date], ["🕐", trip.time], ["👥", `${trip.passengers} راكب`]].map(([ic, v], i) => (
            <div key={i} style={{ background: T.surfAlt, borderRadius: 8, padding: "6px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 14 }}>{ic}</div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Price & deposit */}
        {trip.agreedPrice > 0 && (
          <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 13, color: T.muted }}>سعر الرحلة: <b style={{ color: T.accent }}>{trip.agreedPrice} ج.م</b></span>
            {trip.depositAmount > 0 && (
              <span style={{ fontSize: 13, color: T.muted }}>• ديبوزت: <b style={{ color: T.purple }}>{trip.depositAmount} ج.م</b></span>
            )}
          </div>
        )}

        {/* Notes */}
        {trip.notes && (
          <div style={{ fontSize: 12, color: T.muted, padding: "7px 10px", background: "#FEF9EC", borderRadius: 8, borderRight: `3px solid ${T.gold}`, marginBottom: 12 }}>
            📝 {trip.notes}
          </div>
        )}

        {/* CLIENT SECTIONS */}
        {role === "client" && trip.status === "pending" && trip.offers?.length > 0 && (
          <OffersList
            offers={trip.offers}
            captainProfiles={captainProfiles}
            depositFixed={settings.depositFixed || 100}
            onSelect={handleSelectCaptain}
            onViewCar={cap => setCarModal(cap)}
            busy={busy}
          />
        )}

        {role === "client" && trip.status === "awaiting_deposit" && (
          <DepositSection
            trip={trip} settings={settings}
            txFile={txFile} setTxFile={setTxFile}
            onSubmit={handleSubmitProof} busy={busy}
          />
        )}

        {trip.status === "deposit_review" && (
          <div style={{ background: "#F5F3FF", borderRadius: 12, padding: "12px 16px", marginBottom: 12, border: "1px solid #DDD6FE" }}>
            <div style={{ color: T.purple, fontWeight: 700 }}>🔍 صورة التحويل قيد المراجعة</div>
          </div>
        )}

        {trip.selectedCaptain && trip.status !== "pending" && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "#F0FDF4", borderRadius: 12, border: "1px solid #BBF7D0", marginBottom: isConfirmed ? 8 : 0 }}>
              {selCap?.photoUrl
                ? <img src={selCap.photoUrl} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                : <Avatar name={trip.captainName} size={36} color={T.success} />
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{trip.captainName}</div>
                {selCap && <div style={{ fontSize: 11, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selCap.car} • {selCap.plate}</div>}
              </div>
              {selCap?.carPhotoUrls?.length > 0 && (
                <button onClick={() => setCarModal(selCap)} style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surfAlt, cursor: "pointer", fontSize: 11, fontFamily: "inherit", fontWeight: 600, flexShrink: 0 }}>
                  🚗 صور
                </button>
              )}
            </div>

            {isConfirmed && role === "client" && selCap?.phone && (
              <div style={{ display: "flex", gap: 8 }}>
                <a href={`tel:${selCap.phone}`} style={contactStyle("#10B981", "#059669")}>📞 اتصل بالكابتن</a>
                <a href={`https://wa.me/2${selCap.phone.replace(/^0/, "")}`} target="_blank" rel="noreferrer" style={contactStyle("#25D366", "#128C7E")}>💬 واتساب</a>
              </div>
            )}

            {isConfirmed && role === "captain" && trip.selectedCaptain === user?.id && trip.clientPhone && (
              <div style={{ display: "flex", gap: 8 }}>
                <a href={`tel:${trip.clientPhone}`} style={contactStyle("#10B981", "#059669")}>📞 اتصل بالعميل</a>
                <a href={`https://wa.me/2${trip.clientPhone.replace(/^0/, "")}`} target="_blank" rel="noreferrer" style={contactStyle("#25D366", "#128C7E")}>💬 واتساب</a>
              </div>
            )}
          </div>
        )}

        {role === "client" && trip.status === "completed" && !trip.rating && (
          <div style={{ background: "#FFFBEB", borderRadius: 10, padding: "12px 14px", marginBottom: 12, border: "1px solid #FDE68A" }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>⭐ قيّم تجربتك</div>
            <Stars rating={rateVal} onRate={setRateVal} size={22} />
            {rateVal > 0 && (
              <Button size="sm" style={{ marginTop: 10 }} loading={busy} disabled={busy} onClick={handleRate}>
                إرسال التقييم
              </Button>
            )}
          </div>
        )}

        {/* CAPTAIN SECTIONS */}
        {role === "captain" && trip.status === "pending" && !myOffer && (
          searchLeft > 0 ? (
            showForm ? (
              <OfferForm
                price={offerPrice} setPrice={setOfferPrice}
                note={offerNote} setNote={setOfferNote}
                onSubmit={handleMakeOffer}
                onCancel={() => { setShowForm(false); setOfferPrice(""); setOfferNote(""); }}
                busy={busy} depositFixed={settings.depositFixed || 100}
              />
            ) : (
              <Button full loading={busy} disabled={busy} onClick={() => setShowForm(true)}>💰 تقديم عرض</Button>
            )
          ) : (
            <div style={{ textAlign: "center", padding: "10px", color: T.muted, background: T.surfAlt, borderRadius: 10, fontSize: 12 }}>
              ⌛ انتهى وقت استقبال العروض
            </div>
          )
        )}

        {role === "captain" && trip.status === "pending" && myOffer && (
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: "#FFF7ED", border: "1px solid #FED7AA", fontSize: 13 }}>
            <span style={{ fontWeight: 700, color: T.accent }}>عرضك: {myOffer.price} ج.م</span>
            <span style={{ color: T.muted }}>⏳ في انتظار الرد</span>
          </div>
        )}

        {role === "captain" && trip.selectedCaptain === user?.id && trip.status !== "pending" && (
          <div style={{ padding: "12px 14px", borderRadius: 10, background: "#F0FDF4", border: "1px solid #BBF7D0", marginBottom: 10 }}>
            <div style={{ fontWeight: 700, color: T.success, marginBottom: 4 }}>✅ تم اختيارك لهذه الرحلة</div>
            <div style={{ fontSize: 13, color: T.muted }}>السعر المتفق: <b style={{ color: T.accent }}>{trip.agreedPrice} ج.م</b></div>
            {trip.status === "awaiting_deposit" && <div style={{ color: T.info, marginTop: 4, fontSize: 12 }}>⏳ في انتظار دفع الديبوزت من العميل</div>}
            {trip.status === "deposit_review" && <div style={{ color: T.purple, marginTop: 4, fontSize: 12 }}>🔍 التحويل قيد المراجعة</div>}
            {trip.status === "confirmed" && <div style={{ color: T.success, marginTop: 4, fontSize: 12 }}>🟢 الرحلة مؤكدة — جاهز للانطلاق</div>}
            {trip.status === "captain_completed" && <div style={{ color: "#EAB308", marginTop: 4, fontSize: 12 }}>⏳ في انتظار تأكيد الإتمام من رايدو</div>}
            {trip.status === "completed" && <div style={{ color: T.info, marginTop: 4, fontSize: 12 }}>🏁 تم إتمام الرحلة بنجاح</div>}
          </div>
        )}

        {role === "captain" && trip.selectedCaptain === user?.id && trip.status === "confirmed" && (
          <Button variant="success" full loading={busy} disabled={busy} onClick={() => doAction(captainCompleteTrip, trip.id)}>
            🏁 أتممت الرحلة — طلب تأكيد الإتمام
          </Button>
        )}

        {/* ADMIN SECTIONS */}
        {role === "admin" && trip.status === "deposit_review" && (
          <div style={{ background: "#F5F3FF", borderRadius: 12, padding: 14, marginBottom: 8, border: "1px solid #DDD6FE" }}>
            <div style={{ fontWeight: 700, color: T.purple, marginBottom: 10 }}>🔍 مراجعة إيصال التحويل</div>
            {trip.transferProofUrl ? (
              <div style={{ marginBottom: 12 }}>
                <a href={trip.transferProofUrl} target="_blank" rel="noreferrer">
                  <img src={trip.transferProofUrl} alt="إيصال" style={{ width: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 10, border: `1px solid ${T.border}`, background: T.surfAlt, display: "block" }} />
                  <div style={{ fontSize: 11, color: T.purple, textAlign: "center", marginTop: 4 }}>اضغط لفتح الصورة كاملة ↗</div>
                </a>
              </div>
            ) : (
              <div style={{ background: T.surfAlt, borderRadius: 8, padding: 12, textAlign: "center", color: T.muted, marginBottom: 12, fontSize: 13 }}>⚠️ لم ترفع صورة التحويل بعد</div>
            )}
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 10 }}>مبلغ الديبوزت: <b style={{ color: T.accent }}>{trip.depositAmount} ج.م</b></div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="success" style={{ flex: 1 }} loading={busy} disabled={busy} onClick={() => doAction(approveDeposit, trip.id)}>✓ قبول وتأكيد</Button>
              <Button variant="danger" size="sm" loading={busy} disabled={busy} onClick={() => doAction(rejectDeposit, trip.id)}>✕ رفض</Button>
            </div>
          </div>
        )}

        {role === "admin" && trip.status === "captain_completed" && (
          <div style={{ background: "#FFFBEB", borderRadius: 12, padding: "12px 16px", border: "1px solid #FDE68A", marginBottom: 8 }}>
            <div style={{ fontWeight: 700, color: "#92400E", marginBottom: 8 }}>🏁 الكابتن أعلن إتمام الرحلة</div>
            <Button variant="success" full loading={busy} disabled={busy} onClick={() => doAction(completeTrip, trip.id, trip.selectedCaptain)}>✓ تأكيد إتمام الرحلة</Button>
          </div>
        )}

        {role === "admin" && trip.status === "confirmed" && (
          <Button variant="success" full loading={busy} disabled={busy} onClick={() => doAction(completeTrip, trip.id, trip.selectedCaptain)}>
            🏁 إنهاء الرحلة مباشرة
          </Button>
        )}

        {role === "admin" && ["pending", "awaiting_deposit"].includes(trip.status) && (
          <Button variant="danger" size="sm" loading={busy} disabled={busy} onClick={() => doAction(cancelTrip, trip.id)}>✕ إلغاء الرحلة</Button>
        )}
      </Card>
    </>
  );
});

export default TripCard;

// المكونات الفرعية تبقى كما هي مع إضافة حماية الأزرار
function contactStyle(c1, c2) {
  return {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    padding: "9px 12px", borderRadius: 10,
    background: `linear-gradient(135deg,${c1},${c2})`,
    color: "white", textDecoration: "none", fontSize: 13, fontWeight: 700,
  };
}

function OffersList({ offers, captainProfiles, depositFixed, onSelect, onViewCar, busy }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.muted, marginBottom: 8 }}>
        🏷️ عروض الكباتن ({offers.length})
      </div>
      {offers.map(offer => {
        const cap = captainProfiles[offer.captainId];
        return (
          <div key={offer.captainId} style={{ background: "white", borderRadius: 14, padding: 12, marginBottom: 10, border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              {cap?.photoUrl
                ? <img src={cap.photoUrl} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                : <Avatar name={offer.captainName} size={40} color={T.teal} />
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{offer.captainName}</div>
                <div style={{ fontSize: 11, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{offer.carLabel}</div>
                <Stars rating={offer.rating || 0} size={12} />
              </div>
              <div style={{ textAlign: "center", background: "#FFF7ED", borderRadius: 10, padding: "6px 10px", flexShrink: 0 }}>
                <div style={{ fontWeight: 900, fontSize: 18, color: T.accent, lineHeight: 1 }}>{offer.price}</div>
                <div style={{ fontSize: 10, color: T.muted, lineHeight: 1, marginTop: 2 }}>ج.م</div>
              </div>
            </div>

            <div style={{ background: "#F5F3FF", borderRadius: 8, padding: "5px 10px", marginBottom: 8, fontSize: 12, color: T.purple }}>
              💰 الديبوزت المطلوب: <b>{depositFixed} ج.م</b>
            </div>

            {offer.note && (
              <div style={{ fontSize: 12, color: T.muted, marginBottom: 8, padding: "6px 10px", background: T.surfAlt, borderRadius: 8, fontStyle: "italic" }}>
                "{offer.note}"
              </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button variant="success" style={{ flex: 1, minWidth: 120 }} size="sm" loading={busy} disabled={busy} onClick={() => onSelect(offer)}>
                ✓ اختيار الكابتن
              </Button>
              <button
                disabled={busy}
                onClick={() => cap && onViewCar(cap)}
                style={{
                  padding: "7px 12px", borderRadius: 8, flexShrink: 0,
                  border: `1px solid ${cap?.carPhotoUrls?.length ? T.teal : T.border}`,
                  background: cap?.carPhotoUrls?.length ? "#EFF9FF" : T.surfAlt,
                  cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 600,
                  color: cap?.carPhotoUrls?.length ? T.teal : T.muted,
                  display: "flex", alignItems: "center", gap: 4, opacity: busy ? 0.7 : 1
                }}
              >
                🚗 {cap?.carPhotoUrls?.length ? `صور (${cap.carPhotoUrls.length})` : "تفاصيل"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DepositSection({ trip, settings, txFile, setTxFile, onSubmit, busy }) {
  const payLink = settings.instaPayLink
    ? settings.instaPayLink
    : settings.instaPayNumber
      ? `https://ipn.eg/S/${settings.instaPayNumber}`
      : null;

  return (
    <div style={{ background: "#EFF6FF", borderRadius: 14, padding: 16, marginBottom: 12, border: "1px solid #BFDBFE" }}>
      <div style={{ fontWeight: 700, marginBottom: 12, color: "#1E40AF", fontSize: 15 }}>💳 الدفع عبر InstaPay/محفظة</div>
      <div style={{ background: "white", borderRadius: 12, padding: 14, marginBottom: 14, border: "1px solid #BFDBFE" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 3 }}>المبلغ المطلوب</div>
            <div style={{ fontWeight: 900, fontSize: 28, color: T.accent, lineHeight: 1 }}>{trip.depositAmount} <span style={{ fontSize: 16 }}>ج.م</span></div>
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 3 }}>رقم الاستلام</div>
            <div style={{ fontWeight: 800, fontSize: 16, color: T.primary, letterSpacing: 1 }}>{settings.instaPayNumber || "—"}</div>
            <div style={{ fontSize: 11, color: T.muted }}>باسم: {settings.instaPayName}</div>
          </div>
        </div>

        {payLink ? (
          <a
            href={payLink}
            target="_blank"
            rel="noreferrer"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "13px 16px", borderRadius: 12, background: "linear-gradient(135deg,#10B981,#059669)", color: "white", textDecoration: "none", fontWeight: 700, fontSize: 15, marginBottom: 8, boxShadow: "0 4px 14px rgba(16,185,129,.35)", pointerEvents: busy ? 'none' : 'auto', opacity: busy ? 0.7 : 1 }}
          >
            <span style={{ fontSize: 22 }}>💸</span>
            <span>ادفع الآن عبر InstaPay</span>
          </a>
        ) : (
          <div style={{ background: "#FEF3C7", borderRadius: 10, padding: "10px 14px", marginBottom: 8, fontSize: 13, color: "#92400E" }}>
            ⚠️ لم يتم تفعيل رابط الدفع بعد — حوّل يدوياً للرقم أعلاه
          </div>
        )}
        <div style={{ fontSize: 11, color: T.muted, textAlign: "center" }}>بعد الدفع، ارفع صورة الإيصال أدناه</div>
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, color: "#1E40AF", marginBottom: 8 }}>📎 ارفع صورة الإيصال</div>
      <label style={{ display: "flex", flexDirection: "column", alignItems: "center", border: `2px dashed ${txFile ? T.success : "#93C5FD"}`, borderRadius: 12, padding: 14, cursor: busy ? "not-allowed" : "pointer", background: txFile ? "#F0FDF4" : "rgba(255,255,255,.7)", textAlign: "center", marginBottom: 10, transition: "all .2s", opacity: busy ? 0.6 : 1 }}>
        <input disabled={busy} type="file" accept="image/*" style={{ display: "none" }} onChange={e => e.target.files[0] && setTxFile(e.target.files[0])} />
        {txFile
          ? <><div style={{ fontSize: 26 }}>✅</div><span style={{ fontSize: 13, color: T.success, fontWeight: 700 }}>{txFile.name}</span><span style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>اضغط لتغيير الصورة</span></>
          : <><div style={{ fontSize: 30 }}>📷</div><span style={{ fontSize: 13, color: "#1E40AF", fontWeight: 600 }}>اضغط لاختيار صورة الإيصال</span><span style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>PNG / JPG</span></>
        }
      </label>
      <Button full loading={busy} disabled={!txFile || busy} onClick={onSubmit} style={{ opacity: txFile ? 1 : 0.5 }}>
        {txFile ? "📤 إرسال الإيصال للمراجعة" : "اختر صورة الإيصال أولاً"}
      </Button>
    </div>
  );
}

function OfferForm({ price, setPrice, note, setNote, onSubmit, onCancel, busy, depositFixed }) {
  return (
    <div style={{ background: T.surfAlt, borderRadius: 12, padding: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.muted, marginBottom: 5, display: "block" }}>السعر (ج.م)</label>
          <input disabled={busy} type="number" placeholder="0" value={price} onChange={e => setPrice(e.target.value)} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${T.border}`, fontFamily: "inherit", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.muted, marginBottom: 5, display: "block" }}>ملاحظة (اختياري)</label>
          <input disabled={busy} type="text" placeholder="..." value={note} onChange={e => setNote(e.target.value)} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${T.border}`, fontFamily: "inherit", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
        </div>
      </div>
      {depositFixed > 0 && (
        <div style={{ fontSize: 12, color: T.purple, marginBottom: 10 }}>
          الديبوزت المطلوب من العميل: <b>{depositFixed} ج.م</b>
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <Button variant="success" style={{ flex: 1 }} loading={busy} disabled={busy} onClick={onSubmit}>إرسال العرض</Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>إلغاء</Button>
      </div>
    </div>
  );
}